import { useState, type FormEvent, type JSX } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
  PasswordInput,
  Spinner,
} from "@mailflare/ui";

import { routes } from "@/features/mailboxes/routes";

import { useSignup } from "./hooks";
import { AuthCard, errorText } from "./components/auth-card";
import { TurnstileWidget } from "./components/turnstile";

export function SignupPage(): JSX.Element {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const signup = useSignup();

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    signup.mutate(
      { email, displayName, password, turnstileToken },
      {
        onSuccess: () => {
          navigate(routes.root, { replace: true });
        },
      },
    );
  };

  return (
    <AuthCard
      title="Create your account"
      description="Self-hosted mail in a few seconds."
      footer={
        <p className="text-muted-foreground">
          Already have an account?{" "}
          <Link className="text-primary underline" to={routes.login}>
            Sign in
          </Link>
        </p>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit}>
        <div className="space-y-1.5">
          <Label htmlFor="signup-name">Display name</Label>
          <Input
            id="signup-name"
            autoComplete="name"
            required
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="signup-email">Email</Label>
          <Input
            id="signup-email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="signup-password">Password</Label>
          <PasswordInput
            id="signup-password"
            autoComplete="new-password"
            minLength={12}
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <p className="text-muted-foreground text-xs">At least 12 characters.</p>
        </div>

        <TurnstileWidget onToken={setTurnstileToken} />

        {signup.error !== null ? (
          <Alert variant="destructive">
            <AlertDescription>{errorText(signup.error)}</AlertDescription>
          </Alert>
        ) : null}

        <Button type="submit" className="w-full" disabled={signup.isPending}>
          {signup.isPending ? <Spinner aria-hidden className="size-4" /> : null}
          Create account
        </Button>
      </form>
    </AuthCard>
  );
}
